export const call = {
  permission: {
    title: "カメラとマイクの許可が必要です",
    camera: "カメラへのアクセス",
    microphone: "マイクへのアクセス",
    description:
      "ビデオ通話を利用するには、ブラウザの設定でカメラとマイクのアクセスを許可してください。",
    descriptionNative: "設定画面から許可を変更できます。",
    openSettings: "設定を開く",
  },
  outgoing: {
    calling: "発信中",
    cancel: "キャンセル",
  },
  busy: {
    title: (args: { name: string | number }) => `${args.name}さんは通話中`,
    titleNoName: "お相手は通話中",
    description: "ただいま別の通話中のため、応答できません。",
    videoLabel: "ビデオ通話",
    voiceLabel: "音声通話",
    videoCallRequest: "ビデオ通話をリクエスト",
    voiceCallRequest: "音声通話をリクエスト",
    sendMessage: "メッセージを送る",
    close: "閉じる",
  },
  incoming: {
    videoCall: "ビデオ通話の着信",
    voiceCall: "音声通話の着信",
    accept: "応答",
    decline: "拒否",
  },
  controls: {
    switchCamera: "カメラ切替",
    chat: "チャット",
    buyPoints: "ポイント購入",
    micOn: "マイクON",
    micOff: "マイクOFF",
    endCall: "通話終了",
    end: "終了",
    remainingMinutes: (args: { minutes: string | number }) =>
      `残り${args.minutes}分`,
  },
  endConfirm: {
    title: "通話を終了しますか？",
    cancel: "キャンセル",
    end: "終了",
  },
  ended: {
    normal: "通話が終了しました",
    partnerHangup: "相手が通話を終了しました",
    pointShortage: "ポイントが不足しています",
    billingFailed: "ポイント消費に失敗しました",
    networkError: "ネットワークエラーが発生しました",
    timeout: "応答がありませんでした",
    declined: "通話が拒否されました",
    streamConnectFailed: "相手に接続できませんでした",
    streamFrozen: "映像の受信が途切れたため通話を終了しました",
    cancelledByUser: "通話をキャンセルしました",
    close: "閉じる",
  },
  endedModal: {
    favorite: "お気に入り",
    videoCallRequest: "ビデオ通話リクエスト（無料）",
    voiceCallRequest: "音声通話リクエスト（無料）",
    sendMessageSuffix: "さんにメッセージを送る",
  },
  request: {
    duplicateError: "5分以内に同じ相手にリクエストを送ることはできません",
    error: "リクエストの送信に失敗しました",
    sent: "通話リクエストを送信しました",
    videoContent: (args: { name: string | number }) =>
      `ビデオ通話をリクエストしました✨\n${args.name}さん、ビデオ通話しましょう♪`,
    voiceContent: (args: { name: string | number }) =>
      `音声通話をリクエストしました✨\n${args.name}さん、音声通話しましょう♪`,
  },
  chat: {
    placeholder: "メッセージ...",
    send: "送信",
  },
  lovense: {
    button: "遠隔バイブ",
    menuTitle: "遠隔バイブメニュー",
    duration: "秒",
    points: "pt",
    types: {
      weak: "弱",
      medium: "中",
      strong: "強",
      pulse: "パルス",
      wave: "ウェーブ",
      fireworks: "花火",
      earthquake: "地震",
      cyclone: "サイクロン",
      tornado: "トルネード",
      meteor: "メテオ",
      full_control: "フルコントロール",
    },
    sentMessage: "送信しました",
    offlineError:
      "相手の遠隔バイブがオフライン状態のため送信できませんでした。",
    activating: "起動中",
    controlling: "コントロール中",
    remaining: "残り",
    intensity: "強度",
  },
  /**
   * 通話の pt 不足で出す文脈モーダル。汎用のポイントストアではなく相手の名前と
   * 顔で見せる。価格もプランも変えず、見せ方だけを変える。
   *
   * 文言はきっかけごとに分ける。切られた直後（call_end）は出来事の説明で成立するが、
   * 再訪系はユーザーが自分から相手を探しに来た瞬間なので、同じ文面を出すと
   * 一度見たものとして反射で閉じられる。「ポイント切れ」「終了」は再訪では使わない。
   *
   * 見出しと CTA で一文が完結する（「◯◯さんとの通話を」→「続ける」）。
   */
  pointShortagePrompt: {
    byTrigger: {
      call_end: {
        /** 一文字ずつ現れる。切れた直後の相手の反応を演出する */
        bubble: "えっ、切れちゃった…？",
        lead: (args: { name: string | number }) => `${args.name}さんとの通話を`,
        cta: "続ける",
      },
      call_retap: {
        bubble: "もう一回、、、どうかな？",
        lead: (args: { name: string | number }) => `${args.name}さんに`,
        cta: "かけ直す",
      },
      chat_revisit: {
        bubble: "戻ってきてくれたの？もう1回...",
        lead: (args: { name: string | number }) => `${args.name}さんと`,
        cta: "もう一度つながる",
      },
      profile_revisit: {
        bubble: "見てる...だけ...?♡",
        lead: (args: { name: string | number }) => `${args.name}さんと`,
        cta: "もう一度つながる",
      },
    },
    /** CTA 下の注記。金額は出さず、ポイントが要ることだけ正直に添える */
    ctaNote: "ポイントが必要です",
    close: "閉じる",
    photoAlt: (args: { name: string | number }) => `${args.name}さんの写真`,
  },
} as const;
